"use client";

/**
 * 상품 카드 컴포넌트
 *
 * 디자인 레퍼런스: faire.com 스타일 + 한국 도매 특성
 * - 보더 없는 클린 카드, 넉넉한 여백
 * - hover 시 이미지 줌 + 하트 찜 버튼 노출
 * - 비로그인 시 가격 숨김 ("판매가 사업자 회원 공개")
 * - 비로그인 찜 클릭 → 로그인 유도 다이얼로그
 */
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Heart } from "lucide-react";

import { trackViewItem } from "@/shared/analytics";
import type { ProductListItem } from "@/shared/api/services/productService";
import { useAuth } from "@/shared/auth/hooks";
import { cn } from "@/lib/utils";

import { useCheckFavorite } from "@/features/shopping/queries/useProductDetail";
import {
  usePopularBrandIds,
  usePopularProductIds,
  useToggleFavorite,
} from "@/features/shopping/queries/useProducts";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export { ProductCardSkeleton } from "@/shared/ui";

interface ProductCardProps {
  product: ProductListItem;
  className?: string;
  /** 위 N개는 eager 로드 (above the fold) */
  priority?: boolean;
}

export function ProductCard({ product, className, priority = false }: ProductCardProps) {
  const { isAuthenticated, user } = useAuth();
  const router = useRouter();
  const toggleFavorite = useToggleFavorite();
  const favoriteQuery = useCheckFavorite(String(product.id));
  const popularProductIds = usePopularProductIds();
  const popularBrandIds = usePopularBrandIds();
  const isPopularProduct = popularProductIds.has(product.id);
  const isPopularBrand = popularBrandIds.has(product.idBrand);
  const isFavorited = favoriteQuery.data ?? false;
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [heartSpring, setHeartSpring] = useState(false);
  const [imgError, setImgError] = useState(false);

  const hasDifferentRetail = product.fixedPrice > product.goodsPrice;

  function handleCardClick() {
    if (!isAuthenticated) return;
    trackViewItem({
      userId: user?.id,
      itemId: product.id,
      itemName: product.name,
      price: product.goodsPrice,
      userGroup: user?.userGroups ?? null,
    });
  }

  function handleFavoriteClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      setLoginDialogOpen(true);
      return;
    }
    setHeartSpring(true);
    setTimeout(() => setHeartSpring(false), 400);
    toggleFavorite.mutate(product.id);
  }

  return (
    <>
      <Link
        href={`/product/${product.id}`}
        className={cn("group block", className)}
        onClick={handleCardClick}
      >
        {/* 이미지 + 찜 버튼 */}
        <div className="relative w-full pt-[100%] overflow-hidden bg-neutral-100">
          {product.imagePath && !imgError ? (
            <Image
              src={product.imagePath}
              alt={product.name}
              fill
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
              priority={priority}
              onError={() => setImgError(true)}
              className="object-cover transition-all duration-500 ease-out group-hover:scale-105"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-neutral-400 text-xs">
              No Image
            </div>
          )}

          {(isPopularProduct || isPopularBrand) && (
            <div className="absolute top-2.5 left-2.5 flex flex-col items-start gap-1">
              {isPopularProduct && (
                <span
                  className="rounded-sm bg-neutral-900/95 px-1.5 py-0.5 text-[10px] font-medium tracking-wider text-white shadow-sm backdrop-blur-sm"
                  title="꾸준하게 재구매가 발생하는 상품입니다."
                >
                  인기
                </span>
              )}
              {isPopularBrand && (
                <span className="rounded-sm bg-white/95 px-1.5 py-0.5 text-[10px] font-medium tracking-wider text-neutral-900 shadow-sm backdrop-blur-sm">
                  인기 브랜드
                </span>
              )}
            </div>
          )}

          {/* 찜 버튼 — hover 시 노출 */}
          <button
            type="button"
            onClick={handleFavoriteClick}
            disabled={toggleFavorite.isPending}
            className={cn(
              "absolute top-2.5 right-2.5 rounded-full bg-white/90 backdrop-blur-sm p-2 shadow-sm",
              "transition-all duration-200",
              "hover:bg-white hover:scale-110 hover:shadow-md",
            )}
            aria-label="찜하기"
          >
            <Heart
              className={cn(
                "h-4 w-4 transition-colors",
                isFavorited ? "fill-neutral-800 text-neutral-800" : "text-neutral-600",
                heartSpring && "animate-heart-spring",
              )}
            />
          </button>

          {/* 할인 뱃지 제거 — 도매에서 goodsPrice < fixedPrice는 할인이 아닌 도매 마진 */}
        </div>

        {/* 상품 정보 */}
        <div className="mt-2.5 space-y-0.5">
          {product["Brand.name"] && (
            <p
              role="link"
              className="text-[11px] text-neutral-500 tracking-wide cursor-pointer hover:text-neutral-900 hover:underline"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                router.push(`/brands/${product.idBrand}`);
              }}
            >
              {product["Brand.name"]}
            </p>
          )}
          <p className="text-[13px] leading-[1.4] text-neutral-800 line-clamp-2">{product.name}</p>

          {/* 가격: 로그인 시에만 노출 */}
          <div className="pt-1">
            {isAuthenticated ? (
              <div className="space-y-0.5">
                <span className="text-sm font-semibold text-foreground">
                  {product.goodsPrice.toLocaleString("ko-KR")}원
                </span>
                {hasDifferentRetail && (
                  <p className="text-[11px] text-neutral-400 line-through">
                    {product.fixedPrice.toLocaleString("ko-KR")}원
                  </p>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-neutral-400">판매가 사업자 회원 공개</p>
            )}
          </div>
        </div>
      </Link>

      {/* 비로그인 찜 → 로그인 유도 */}
      <AlertDialog open={loginDialogOpen} onOpenChange={setLoginDialogOpen}>
        <AlertDialogContent className="sm:rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>로그인이 필요합니다</AlertDialogTitle>
            <AlertDialogDescription>찜 기능은 로그인 후 이용할 수 있습니다.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                router.push(`/login?returnUrl=${encodeURIComponent(`/product/${product.id}`)}`)
              }
            >
              로그인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
